import React from 'react';
import { Card, CardBody, CardHeader } from 'reactstrap';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { Truck } from 'react-feather';

interface VehicleStatusProps {
    data: Array<{ name: string; value: number }>;
}

export default function VehicleStatusChart({ data }: VehicleStatusProps) {
    const statusColors: { [key: string]: string } = {
        'Active': '#10b981',
        'Inactive': '#94a3b8',
        'Maintenance': '#f59e0b',
        'Sold': '#ef4444',
    };

    const totalVehicles = data.reduce((sum, item) => sum + item.value, 0);

    const options: ApexOptions = {
        chart: {
            type: 'donut',
            height: 380,
            animations: {
                enabled: true,
                easing: 'easeinout',
                speed: 800,
            },
        },
        labels: data.map(item => item.name),
        colors: data.map(item => statusColors[item.name] || '#6b7280'),
        legend: {
            position: 'bottom',
            fontSize: '13px',
            fontWeight: 600,
            offsetY: 0,
            itemMargin: {
                horizontal: 10,
                vertical: 5,
            },
        },
        dataLabels: {
            enabled: true,
            formatter: (val: number) => {
                return Math.round(val) + '%';
            },
            style: {
                fontSize: '14px',
                fontWeight: 700,
                colors: ['#fff'],
            },
            dropShadow: {
                enabled: true,
                top: 1,
                left: 1,
                blur: 1,
                opacity: 0.45,
            },
        },
        plotOptions: {
            pie: {
                donut: {
                    size: '80%',
                    labels: {
                        show: true,
                        name: {
                            show: true,
                            fontSize: '14px',
                            fontWeight: 600,
                            color: '#718096',
                            offsetY: -10,
                        },
                        value: {
                            show: true,
                            fontSize: '32px',
                            fontWeight: 700,
                            color: '#2d3748',
                            offsetY: 10,
                            formatter: (val: string) => val,
                        },
                        total: {
                            show: true,
                            label: 'Total Fleet',
                            fontSize: '14px',
                            fontWeight: 600,
                            color: '#718096',
                            formatter: () => {
                                return totalVehicles.toString();
                            },
                        },
                    },
                },
            },
        },
        states: {
            hover: {
                filter: {
                    type: 'lighten',
                    value: 0.1,
                },
            },
            active: {
                filter: {
                    type: 'darken',
                    value: 0.1,
                },
            },
        },
        tooltip: {
            enabled: true,
            theme: 'light',
            y: {
                formatter: (value) => {
                    const percentage = ((value / totalVehicles) * 100).toFixed(1);
                    return `${value} vehicles (${percentage}%)`;
                },
            },
        },
        responsive: [
            {
                breakpoint: 480,
                options: {
                    chart: {
                        height: 320,
                    },
                    legend: {
                        position: 'bottom',
                    },
                },
            },
        ],
    };

    const series = data.map(item => item.value);

    return (
        <Card className="chart-card" style={{ height: '100%' }}>
            <CardHeader className="pb-0">
                <div className="d-flex align-items-start justify-content-between">
                    <div className="flex-grow-1">
                        <h5 className="mb-1">Vehicle Status</h5>
                        <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                            Current fleet distribution
                        </p>
                    </div>
                    <div
                        className="stat-card-icon"
                        style={{
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            boxShadow: '0 4px 14px 0 rgba(16, 185, 129, 0.4)',
                            width: '48px',
                            height: '48px',
                        }}
                    >
                        <Truck size={22} color="white" strokeWidth={2.5} />
                    </div>
                </div>
            </CardHeader>
            <CardBody>
                <ReactApexChart options={options} series={series} type="donut" height={380} />

                {/* Status Summary */}
                <div className="row g-2 mt-2">
                    {data.map((item, index) => (
                        <div key={index} className="col-6">
                            <div
                                className="p-2 rounded d-flex align-items-center"
                                style={{
                                    background: `${statusColors[item.name]}15`,
                                    border: `1px solid ${statusColors[item.name]}30`
                                }}
                            >
                                <div
                                    style={{
                                        width: '8px',
                                        height: '8px',
                                        borderRadius: '50%',
                                        background: statusColors[item.name],
                                        marginRight: '8px'
                                    }}
                                />
                                <div className="flex-grow-1">
                                    <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>
                                        {item.name}
                                    </small>
                                    <strong style={{ fontSize: '0.875rem', color: '#2d3748' }}>
                                        {item.value}
                                    </strong>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </CardBody>
        </Card>
    );
}

